import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Config from './pages/Config';
import Metrics from './pages/Metrics';
import Fraud from './pages/Fraud';
import Roles from './pages/Roles';
import Transactions from './pages/Transactions';
import Withdrawals from './pages/Withdrawals';
import Games from './pages/Games';
import Campaigns from './pages/Campaigns';
import Banks from './pages/Banks';
import Settlement from './pages/Settlement';
import Tasks from './pages/Tasks';
import BioKinetic from './pages/BioKinetic';
import Compliance from './pages/Compliance';
import Leaderboard from './pages/Leaderboard';
import Providers from './pages/Providers';
import Audit from './pages/Audit';
import SystemHealth from './pages/SystemHealth';
import VisaBrief from './pages/VisaBrief';
import Press from './pages/Press';
import Pro from './pages/Pro';
import Setup2FA from './pages/Setup2FA';
import './App.css';

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      {/* Rendered WITHOUT DashboardLayout on purpose: in production the layout
          fires admin calls that all return 403 until 2FA is on, which would bury
          the page under the very errors it exists to fix. */}
      <Route
        path="/setup-2fa"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <Setup2FA />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/users"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Users />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/config"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Config />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/metrics"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Metrics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/fraud"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Fraud />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/roles"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Roles />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/transactions"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Transactions />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/withdrawals"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Withdrawals />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/games"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Games />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/campaigns"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Campaigns />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/banks"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Banks />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settlement"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Settlement />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/tasks"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Tasks />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/biokinetic"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <BioKinetic />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/compliance"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Compliance />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/leaderboard"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Leaderboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/providers"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Providers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/audit"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Audit />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/health"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <SystemHealth />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/brief"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <VisaBrief />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/press"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Press />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/pro"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <Pro />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
